import {useEffect, useMemo, useRef} from 'react'
import {filterConfigOptionsForTypeSelector} from '../../data/filterConfigs.js'
import {updateForm, useAutoForm, useSelector} from '../../store.js'
import Icon from '../icons/Icon.js'
import ButtonPrimary from './Button/ButtonPrimary.js'
import {useZropdown, useZropdownElement} from './Zropdown.js'
import {filterOptions, tokenizeOptions} from '../tokenizeOptions.js'
import {currentDropdownSelector} from '../../redux/selectors/ui/index.js'
import FilterDropdown from './FilterDropdown.js'
import className from '../className.js'

function filterFilterConfigs(dropdown, options, optionTokens, text) {
  try {
    if (!text) {
      updateForm(dropdown, {
        results: options,
        hasFoundNothing: false,
      })

      return
    }

    const results = filterOptions(text, optionTokens)

    updateForm(dropdown, {
      results,
      selectedIndex: 0,
      hasFoundNothing: results.length === 0,
    })
  } catch (err) {
    updateForm(dropdown, {
      serverError: err.message,
    })
  }
}

function Item({
  option,
  isFocused,
  onSelect,
  onEdit,
  onDelete,
  hasManagePermission,
}) {
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <li ref={ref} className="list__item list__item--dropdown">
      <div
        className={className`wrap--dropdown-item flex--justify-nowrap label--selectable label--selectable-pt-pb-0 margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
        }}`}
      >
        <button
          className="btn--link list__link--dropdown list__link--preset"
          type="button"
          onClick={() => onSelect(option)}
        >
          <span className="btn__text">{option.display}</span>
        </button>
        {hasManagePermission && (
          <span className="wrap--additional-buttons">
            <button
              className="btn btn--link no-underline margin-right-3"
              type="button"
              title="Edit Filter"
              onClick={() => onEdit(option)}
            >
              <Icon name="pencil" className="fs-00" />
            </button>
            <button
              className="btn btn--link no-underline"
              type="button"
              title="Delete Filter"
              onClick={() => onDelete(option)}
            >
              <Icon name="trash" className="fs-00" />
            </button>
          </span>
        )}
      </div>
    </li>
  )
}

/**
 * A button to open the filter modal
 *
 * @param {Object} props
 * @param {Function} props.onClick
 * @param {Function} props.onFilterConfigApply
 * @param {Function} props.onFilterConfigCreate
 * @param {Function} props.onFilterConfigEdit
 * @param {Function} props.onFilterConfigDelete
 * @param {Boolean} props.hasManageFilterConfigPermission
 * @param {'order'|'product'} props.type
 * @returns {ReactNode}
 */
export default function FiltersButton({
  onClick,
  onFilterConfigApply,
  onFilterConfigCreate,
  onFilterConfigEdit,
  onFilterConfigDelete,
  hasManageFilterConfigPermission,
  type,
}) {
  const dropdown = `filters_button__${type}`
  const currentDropdown = useSelector(currentDropdownSelector)
  const options = useSelector((state) =>
    filterConfigOptionsForTypeSelector(state, {type}),
  )
  const optionTokens = useMemo(() => tokenizeOptions(options), [options])
  const form = useAutoForm(dropdown, {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    serverError: null,
    hasChanged: false,
  })

  useEffect(() => {
    filterFilterConfigs(dropdown, options, optionTokens, form && form.text)
  }, [options, form && form.text])

  const buttonRef = useRef()

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onSelect: (option) => {
        onFilterConfigApply(option)

        updateForm(dropdown, {hasChanged: true})
      },
    })

  const noPermissionsAndNoSavedFilters =
    !hasManageFilterConfigPermission && options.length === 0
  const showSavedFilters = type && !noPermissionsAndNoSavedFilters

  return (
    <>
      <div className="flex align-items-center">
        <div>
          <ButtonPrimary isOutlined size="xx-sm" onClick={onClick}>
            <Icon
              name="sliders"
              className="fs-00 lh-0 v-align-middle margin-right-3"
            />
            <span className="fs-n1 lh-md v-align-middle">Filters</span>
          </ButtonPrimary>
        </div>
        {showSavedFilters && (
          <div
            className={`wrap-outer--tag-dropdown-with-filter lh-sm ${
              currentDropdown === dropdown ? 'open' : ''
            }`}
            data-dropdown={dropdown}
          >
            <button
              id={dropdown}
              ref={buttonRef}
              className="btn btn--action-dropdown margin-left-3"
              type="button"
            >
              <Icon name="meatballs" className="fs-02 lh-0 v-align-middle" />
            </button>
            <FilterDropdown
              dropdown={dropdown}
              form={form}
              onChangeInternal={onChangeInternal}
              onConfirm={onConfirm}
              onUp={onUp}
              onDown={onDown}
              placeholder="Search your saved filter sets..."
              includeSearchInput
              parentRef={buttonRef}
              footer={
                onFilterConfigCreate && hasManageFilterConfigPermission ? (
                  <div className="list__item--dropdown  list__item--dropdown-last">
                    <button
                      className="btn--link list__link list__link--dropdown"
                      type="button"
                      onClick={() => onFilterConfigCreate()}
                    >
                      Save a Filter Set
                    </button>
                  </div>
                ) : null
              }
            >
              <li className="list__item list__item--dropdown list__item--dropdown-header">
                <strong>SAVED FILTER SETS</strong>
              </li>
              {form.results.map((option, index) => (
                <Item
                  key={option.value}
                  option={option}
                  isFocused={index === form.selectedIndex}
                  onSelect={onSelectInternal}
                  onEdit={onFilterConfigEdit}
                  onDelete={onFilterConfigDelete}
                  hasManagePermission={hasManageFilterConfigPermission}
                />
              ))}
              {form.hasFoundNothing && (
                <li className="list__item--dropdown list__item--dropdown-empty align-center">
                  No saved filter sets match your search.
                </li>
              )}
              {options.length === 0 && (
                <li className="list__item--dropdown list__item--dropdown-empty align-center">
                  You haven’t saved any filter sets yet!
                </li>
              )}
            </FilterDropdown>
          </div>
        )}
      </div>
    </>
  )
}
